import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, ENDPOINTS } from '../config/api';
import { getAccess, setAccess, setRefresh, clearTokens } from '../services/apiService';
import { effectivePerms } from '../config/permissions';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const token = getAccess();
      const resp = await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.ME}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        setUser(data);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const resp = await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.LOGIN}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || 'Login failed');
    }

    const data = await resp.json();
    setAccess(data.access_token);
    setRefresh(data.refresh_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.LOGOUT}`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {}
    clearTokens();
    setUser(null);
    window.location.href = '/login';
  };

  const isOwner = user?.role === 'owner';
  const isAccountant = user?.role === 'accountant';
  const isBranchManager = user?.role === 'branch_manager';
  const isSales = user?.role === 'sales';
  // Platform admin (operates the whole SaaS, across all organizations).
  // Determined by the `is_admin` flag on the user, not by their org role.
  const isSuperAdmin = user?.is_admin === true;

  // Per-user permission check. Owner always allowed; otherwise role defaults
  // overlaid with any explicit overrides the owner set (returned by the backend).
  const can = useCallback((key) => {
    if (!user) return false;
    if (user.role === 'owner') return true;
    return effectivePerms(user)[key] !== false;
  }, [user]);

  // Buying-price visibility is now a permission. Defaults match the old rule
  // (owner + accountant) but the owner can grant it to anyone.
  const canSeeBuyingPrice = can('price.buying.view');

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, fetchMe,
      isOwner, isAccountant, isBranchManager, isSales, isSuperAdmin, canSeeBuyingPrice, can,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
